import { useState, useMemo } from 'react';
import { X, Plus, Minus, Trash2, Search, UtensilsCrossed, Loader2, Pencil } from 'lucide-react';
import { useMenuQuery, type MenuItem } from '../hooks/useMenuQuery';
import type { Order, OrderItem } from '../types';

interface ModifyOrderModalProps {
  order: Order;
  shopId: string;
  onSave: (items: OrderItem[]) => Promise<void>;
  onClose: () => void;
  isSaving: boolean;
}

interface ItemCustomization {
  menuItem: MenuItem;
  selectedSize?: { nameAr: string; price: number };
  selectedVariant?: { id: string; nameAr: string; priceModifier: number };
  selectedMilk?: { id: string; nameAr: string; priceModifier: number };
  editingIndex?: number; // Track which item we're editing (undefined = adding new)
}

export function ModifyOrderModal({ order, shopId, onSave, onClose, isSaving }: ModifyOrderModalProps) {
  const { data: menuData, isLoading: isMenuLoading } = useMenuQuery(shopId);
  const [items, setItems] = useState<OrderItem[]>([...order.items]);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);
  const [customizingItem, setCustomizingItem] = useState<ItemCustomization | null>(null);

  // Calculate total amount
  const totalAmount = useMemo(() => {
    return items.reduce((sum, item) => {
      let itemTotal = item.unitPrice * item.quantity;
      if (item.size) itemTotal = item.size.price * item.quantity;
      if (item.variant) itemTotal += item.variant.priceModifier * item.quantity;
      if (item.milk) itemTotal += item.milk.priceModifier * item.quantity;
      return sum + itemTotal;
    }, 0);
  }, [items]);

  // Filter menu items by search query
  const filteredMenu = useMemo(() => {
    if (!menuData?.menu) return [];
    if (!searchQuery.trim()) return menuData.menu;

    const query = searchQuery.toLowerCase();
    return menuData.menu.map(category => ({
      ...category,
      items: category.items.filter(item =>
        item.nameAr.toLowerCase().includes(query) ||
        item.nameEn?.toLowerCase().includes(query)
      )
    })).filter(category => category.items.length > 0);
  }, [menuData?.menu, searchQuery]);

  // Update item quantity
  const updateQuantity = (index: number, delta: number) => {
    setItems(prev => {
      const newItems = [...prev];
      const newQty = newItems[index].quantity + delta;
      if (newQty <= 0) {
        newItems.splice(index, 1);
      } else if (newQty <= 99) {
        newItems[index] = { ...newItems[index], quantity: newQty };
      }
      return newItems;
    });
  };

  // Remove item
  const removeItem = (index: number) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  // Start adding item (show customization if needed)
  const startAddItem = (menuItem: MenuItem) => {
    const hasSizes = menuItem.sizes && menuItem.sizes.length > 0;
    const hasVariants = menuItem.variants && menuItem.variants.length > 0;
    const hasMilk = menuData?.milkOptions && menuData.milkOptions.length > 0;

    if (hasSizes || hasVariants || hasMilk) {
      // Show customization modal
      setCustomizingItem({
        menuItem,
        selectedSize: hasSizes ? menuItem.sizes[0] : undefined,
      });
    } else {
      // Add directly
      addItemToOrder(menuItem);
    }
  };

  // Start editing existing item
  const startEditItem = (index: number) => {
    const item = items[index];

    // Find the menu item from menu data
    let menuItem: MenuItem | undefined;
    for (const category of menuData?.menu || []) {
      menuItem = category.items.find(mi => mi.id === item.menuItemId);
      if (menuItem) break;
    }

    if (!menuItem) {
      // Item not found in menu (might have been removed)
      // Create a minimal menuItem for editing
      menuItem = {
        id: item.menuItemId,
        nameAr: item.nameAr,
        nameEn: item.nameEn,
        price: String(item.unitPrice),
        sizes: item.size ? [item.size] : [],
        variants: item.variant ? [item.variant] : [],
        isAvailable: true,
      };
    }

    setCustomizingItem({
      menuItem,
      selectedSize: item.size,
      selectedVariant: item.variant,
      selectedMilk: item.milk,
      editingIndex: index,
    });
  };

  // Update item at index with new options
  const updateItemOptions = (
    index: number,
    size?: { nameAr: string; price: number },
    variant?: { id: string; nameAr: string; priceModifier: number },
    milk?: { id: string; nameAr: string; priceModifier: number }
  ) => {
    setItems(prev => {
      const newItems = [...prev];
      const item = newItems[index];
      const basePrice = size ? size.price : item.unitPrice;

      newItems[index] = {
        ...item,
        unitPrice: basePrice,
        size: size,
        variant: variant,
        milk: milk,
      };
      return newItems;
    });
  };

  // Add item to order
  const addItemToOrder = (
    menuItem: MenuItem,
    size?: { nameAr: string; price: number },
    variant?: { id: string; nameAr: string; priceModifier: number },
    milk?: { id: string; nameAr: string; priceModifier: number }
  ) => {
    const basePrice = size ? size.price : parseInt(menuItem.price);

    // Check if same item with same options exists
    const existingIndex = items.findIndex(item =>
      item.menuItemId === menuItem.id &&
      item.size?.nameAr === size?.nameAr &&
      item.variant?.id === variant?.id &&
      item.milk?.id === milk?.id
    );

    if (existingIndex >= 0) {
      // Increment quantity
      updateQuantity(existingIndex, 1);
    } else {
      // Add new item
      const newItem: OrderItem = {
        menuItemId: menuItem.id,
        nameAr: menuItem.nameAr,
        nameEn: menuItem.nameEn,
        quantity: 1,
        unitPrice: basePrice,
        imageUrl: menuItem.imageUrl,
        ...(size && { size }),
        ...(variant && { variant }),
        ...(milk && { milk }),
      };
      setItems(prev => [...prev, newItem]);
    }
  };

  // Handle save
  const handleSave = async () => {
    if (items.length === 0) return;
    await onSave(items);
  };

  // Customization modal
  const renderCustomizationModal = () => {
    if (!customizingItem) return null;
    const { menuItem, selectedSize, selectedVariant, selectedMilk } = customizingItem;
    const hasSizes = menuItem.sizes && menuItem.sizes.length > 0;
    const hasVariants = menuItem.variants && menuItem.variants.length > 0;
    const milkOptions = menuData?.milkOptions || [];

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="w-full max-w-md rounded-2xl border border-border bg-surface-light p-6">
          <h3 className="text-lg font-bold text-white mb-4">{menuItem.nameAr}</h3>

          {/* Sizes */}
          {hasSizes && (
            <div className="mb-4">
              <p className="text-sm text-gray-400 mb-2">الحجم</p>
              <div className="flex flex-wrap gap-2">
                {menuItem.sizes.map((size) => (
                  <button
                    key={size.nameAr}
                    onClick={() => setCustomizingItem(prev => prev ? { ...prev, selectedSize: size } : null)}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      selectedSize?.nameAr === size.nameAr
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface text-gray-300 hover:bg-surface-light'
                    }`}
                  >
                    {size.nameAr}
                    <span className="text-xs mr-1 opacity-70">
                      ({size.price.toLocaleString('ar-IQ')})
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Variants */}
          {hasVariants && (
            <div className="mb-4">
              <p className="text-sm text-gray-400 mb-2">الإضافات</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setCustomizingItem(prev => prev ? { ...prev, selectedVariant: undefined } : null)}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    !selectedVariant
                      ? 'bg-primary-500 text-white'
                      : 'bg-surface text-gray-300 hover:bg-surface-light'
                  }`}
                >
                  بدون
                </button>
                {menuItem.variants.map((variant) => (
                  <button
                    key={variant.id}
                    onClick={() => setCustomizingItem(prev => prev ? { ...prev, selectedVariant: variant } : null)}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      selectedVariant?.id === variant.id
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface text-gray-300 hover:bg-surface-light'
                    }`}
                  >
                    {variant.nameAr}
                    {variant.priceModifier > 0 && (
                      <span className="text-xs mr-1 opacity-70">
                        (+{variant.priceModifier.toLocaleString('ar-IQ')})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Milk Options */}
          {milkOptions.length > 0 && (
            <div className="mb-4">
              <p className="text-sm text-gray-400 mb-2">الحليب</p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setCustomizingItem(prev => prev ? { ...prev, selectedMilk: undefined } : null)}
                  className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                    !selectedMilk
                      ? 'bg-primary-500 text-white'
                      : 'bg-surface text-gray-300 hover:bg-surface-light'
                  }`}
                >
                  عادي
                </button>
                {milkOptions.map((milk) => (
                  <button
                    key={milk.id}
                    onClick={() => setCustomizingItem(prev => prev ? { ...prev, selectedMilk: milk } : null)}
                    className={`px-4 py-2 rounded-lg text-sm transition-colors ${
                      selectedMilk?.id === milk.id
                        ? 'bg-primary-500 text-white'
                        : 'bg-surface text-gray-300 hover:bg-surface-light'
                    }`}
                  >
                    {milk.nameAr}
                    {milk.priceModifier > 0 && (
                      <span className="text-xs mr-1 opacity-70">
                        (+{milk.priceModifier.toLocaleString('ar-IQ')})
                      </span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 mt-6">
            <button
              onClick={() => setCustomizingItem(null)}
              className="flex-1 rounded-lg border border-border bg-surface py-3 text-gray-300 hover:bg-surface-light"
            >
              إلغاء
            </button>
            <button
              onClick={() => {
                if (customizingItem.editingIndex !== undefined) {
                  // Editing existing item
                  updateItemOptions(customizingItem.editingIndex, selectedSize, selectedVariant, selectedMilk);
                } else {
                  // Adding new item
                  addItemToOrder(menuItem, selectedSize, selectedVariant, selectedMilk);
                }
                setCustomizingItem(null);
              }}
              className="flex-1 rounded-lg bg-primary-500 py-3 font-medium text-white hover:bg-primary-600"
            >
              {customizingItem.editingIndex !== undefined ? 'حفظ' : 'إضافة'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/70">
      <div className="flex h-[90vh] w-[95vw] max-w-5xl flex-col rounded-2xl border border-border bg-surface overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 className="text-xl font-bold text-white">
            تعديل الطلب #{order.queueNumber}
          </h2>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="rounded-lg p-2 text-gray-400 hover:bg-surface-light hover:text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex flex-1 overflow-hidden">
          {/* Left Panel - Current Order */}
          <div className="flex w-1/2 flex-col border-l border-border">
            <div className="border-b border-border px-4 py-3">
              <h3 className="font-semibold text-gray-400">الطلب الحالي</h3>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <UtensilsCrossed className="h-12 w-12 mb-3 opacity-50" />
                  <p>لا توجد منتجات في الطلب</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {items.map((item, index) => (
                    <div
                      key={`${item.menuItemId}-${index}`}
                      className="rounded-xl border border-border bg-surface-light p-3"
                    >
                      <div className="flex items-start gap-3 mb-2">
                        {/* Item thumbnail */}
                        <div className="w-12 h-12 rounded-xl bg-surface overflow-hidden shrink-0 border border-border/30">
                          {item.imageUrl ? (
                            <img src={item.imageUrl} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-surface">
                              <UtensilsCrossed className="h-6 w-6 text-gray-500" />
                            </div>
                          )}
                        </div>
                        {/* Item details */}
                        <button
                          onClick={() => startEditItem(index)}
                          className="flex-1 text-right group cursor-pointer hover:bg-surface/50 rounded-lg p-1 -m-1 transition-colors"
                        >
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white">{item.nameAr}</p>
                            <Pencil className="h-3 w-3 text-gray-500 opacity-0 group-hover:opacity-100 transition-opacity" />
                          </div>
                          {(item.size || item.variant || item.milk) && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {item.size && (
                                <span className="text-xs px-2 py-0.5 rounded bg-blue-500/20 text-blue-400">
                                  {item.size.nameAr}
                                </span>
                              )}
                              {item.variant && (
                                <span className="text-xs px-2 py-0.5 rounded bg-purple-500/20 text-purple-400">
                                  {item.variant.nameAr}
                                </span>
                              )}
                              {item.milk && (
                                <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">
                                  {item.milk.nameAr}
                                </span>
                              )}
                            </div>
                          )}
                        </button>
                        <button
                          onClick={() => removeItem(index)}
                          className="p-1 text-red-400 hover:bg-red-500/20 rounded shrink-0"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => updateQuantity(index, -1)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface text-gray-400 hover:bg-surface hover:text-white"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                          <span className="w-8 text-center text-lg font-bold text-white">
                            {item.quantity}
                          </span>
                          <button
                            onClick={() => updateQuantity(index, 1)}
                            className="flex h-8 w-8 items-center justify-center rounded-lg bg-surface text-gray-400 hover:bg-surface hover:text-white"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>
                        <span className="text-gray-400">
                          {(() => {
                            let price = item.unitPrice * item.quantity;
                            if (item.size) price = item.size.price * item.quantity;
                            if (item.variant) price += item.variant.priceModifier * item.quantity;
                            if (item.milk) price += item.milk.priceModifier * item.quantity;
                            return price.toLocaleString('ar-IQ');
                          })()} د.ع
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            {/* Total */}
            <div className="border-t border-border px-4 py-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">المجموع</span>
                <span className="text-xl font-bold text-primary-500">
                  {totalAmount.toLocaleString('ar-IQ')} د.ع
                </span>
              </div>
            </div>
          </div>

          {/* Right Panel - Menu */}
          <div className="flex w-1/2 flex-col">
            <div className="border-b border-border px-4 py-3">
              <h3 className="font-semibold text-gray-400 mb-2">القائمة</h3>
              <div className="relative">
                <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" />
                <input
                  type="text"
                  placeholder="بحث..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-border bg-surface py-2 pr-10 pl-4 text-white placeholder-gray-500 focus:border-primary-500 focus:outline-none"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              {isMenuLoading ? (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="h-8 w-8 animate-spin text-primary-500" />
                </div>
              ) : filteredMenu.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <p>لا توجد نتائج</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {filteredMenu.map((category) => (
                    <div key={category.id} className="rounded-xl border border-border overflow-hidden">
                      <button
                        onClick={() => setExpandedCategory(
                          expandedCategory === category.id ? null : category.id
                        )}
                        className="w-full flex items-center justify-between px-4 py-3 bg-surface-light text-white font-medium hover:bg-surface"
                      >
                        <span>{category.nameAr}</span>
                        <span className="text-sm text-gray-500">
                          {category.items.filter(i => i.isAvailable).length} منتج
                        </span>
                      </button>
                      {expandedCategory === category.id && (
                        <div className="divide-y divide-border/50">
                          {category.items
                            .filter(item => item.isAvailable)
                            .map((item) => (
                              <button
                                key={item.id}
                                onClick={() => startAddItem(item)}
                                className="w-full flex items-center justify-between px-4 py-3 text-right hover:bg-surface-light transition-colors"
                              >
                                <div className="flex-1">
                                  <p className="text-white">{item.nameAr}</p>
                                  {item.sizes && item.sizes.length > 0 && (
                                    <p className="text-xs text-gray-500">
                                      {item.sizes.map(s => s.nameAr).join(' • ')}
                                    </p>
                                  )}
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="text-sm text-gray-400">
                                    {item.sizes && item.sizes.length > 0
                                      ? `من ${parseInt(item.price).toLocaleString('ar-IQ')}`
                                      : parseInt(item.price).toLocaleString('ar-IQ')
                                    } د.ع
                                  </span>
                                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary-500/20 text-primary-500">
                                    <Plus className="h-4 w-4" />
                                  </div>
                                </div>
                              </button>
                            ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t border-border px-6 py-4">
          <button
            onClick={onClose}
            disabled={isSaving}
            className="rounded-xl border border-border px-8 py-3 text-gray-300 hover:bg-surface-light disabled:opacity-50"
          >
            إلغاء
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving || items.length === 0}
            className="flex items-center gap-2 rounded-xl bg-primary-500 px-8 py-3 font-bold text-white hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-5 w-5 animate-spin" />
                <span>جاري الحفظ...</span>
              </>
            ) : (
              <span>حفظ التعديلات</span>
            )}
          </button>
        </div>
      </div>

      {/* Customization Modal */}
      {renderCustomizationModal()}
    </div>
  );
}
